import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { deleteSale, fetchSales, patchSale, createProductSale } from "./thunks";
import { Sale } from "@/types";

interface PaginatedSales {
  data: Sale[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface SalesState {
  sales: PaginatedSales;
  loading: boolean;
  error: string | null;
}

const initialState: SalesState = {
  sales: { data: [] },
  loading: false,
  error: null,
};

const SalesSlice = createSlice({
  name: "sales",
  initialState,
  reducers: {
    clearSales: (state) => {
      state.sales = { data: [] };
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSales.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        const payload = action.payload;

        if (Array.isArray(payload.data)) {
          // Dados não paginados
          state.sales = { data: payload.data };
        } else {
          // Dados paginados
          state.sales = {
            data: payload.data.data,
            total: payload.data.total,
            currentPage: payload.data.current_page,
            perPage: payload.data.per_page,
          };
        }
      })
      .addCase(fetchSales.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })

      // Delete
      .addCase(deleteSale.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSale.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.sales.data = state.sales.data.filter(
          (s) => String(s.id) != action.payload.data.id,
        );
        toast.success("Venda excluída com sucesso!");
      })
      .addCase(deleteSale.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })

      // Patch
      .addCase(patchSale.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(patchSale.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        const index = state.sales.data.findIndex(
          (s) => s.id == action.payload.data.id,
        );
        if (index !== -1) {
          // Merge dos dados antigos com os novos
          state.sales.data[index] = {
            ...state.sales.data[index],
            ...action.payload.data,
          };
        }
        toast.success("Baixa de venda realizada com sucesso!");
      })
      .addCase(patchSale.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        console.log("error", state.error);
        toast.error("Error");
      })

      // Create product sale
      .addCase(createProductSale.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createProductSale.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          // Optionally push new sale into list if needed
          const newSale = action.payload?.data;
          if (newSale) {
            const current = Array.isArray(state.sales.data)
              ? state.sales.data
              : [];
            state.sales.data = [newSale, ...current];
          }
          toast.success("Venda de produtos realizada com sucesso!");
        },
      )
      .addCase(createProductSale.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error(state.error || "Falha ao realizar venda de produtos");
      });
  },
});

export default SalesSlice.reducer;
